
import Vue from 'vue';
import UserMenu from '@/components/UserMenu.vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { db } from '@/plugins/firebase';
import { bindFirebaseRef } from '@/plugins/bindFirebaseRef';
import { getModule } from 'vuex-module-decorators';
import Transcription from '@/store/modules/transcription';
import store from '@/store/store';
import Auth from '@/store/modules/auth';
import OpenSeaDragon from '@/components/transcription/OpenSeaDragon.vue';
import router from '@/router';
import StartTranscriptionDialog from '../components/transcription/StartTranscriptionDialog.vue';
import CompleteTranscriptionDialog from '../components/transcription/CompleteTranscriptionDialog.vue';
import Diff from 'diff';
import ShareTranscriptionDialog from '@/components/transcription/ShareTranscriptionDialog.vue';
import { Collection, ITranscription, Project } from '../types';

const trscModule = getModule(Transcription, store);
const authModule = getModule(Auth, store);

@Component({
  components: {
    UserMenu,
    OpenSeaDragon,
    StartTranscriptionDialog,
    CompleteTranscriptionDialog,
    ShareTranscriptionDialog,
  },
})
export default class TranscriptionLayout extends Vue {
  transcription: any = {};
  showStartDialog = false;
  showCompleteDialog = false;
  showShareDialog = false;
  count = 0;
  project: Project | null = null;
  collection: Collection | null = null;

  title() {
    const label = this.processLabel(trscModule.entry.label);
    return `${label}-コマ${this.$route.params.page}`;
  }

  get pages() {
    return trscModule.transcriptions.map((t) => {
      const label = this.statusLabelFor(t);
      return `${t.index + 1}（${label}）`;
    });
  }

  get currentPage() {
    return parseInt(this.$route.params.page);
  }

  set currentPage(p) {
    this.$router.push({
      name: 'transcription.view',
      params: {
        entryId: this.$route.params.entryId,
        page: p + '',
      },
    });
  }

  get currentUser() {
    return authModule.currentUser;
  }

  get isCurrentUserEditing(): boolean {
    if (!trscModule.currentTranscription) return false;
    return trscModule.isCurrentUserEditing;
  }

  get entry() {
    return trscModule.entry;
  }

  get statusLabel() {
    return this.statusLabelFor(trscModule.transcription);
  }

  get isEditable() {
    return trscModule.isEditable;
  }

  get transcriptionId() {
    return trscModule.transcription.id;
  }

  get ref() {
    return this.$route.query.ref as string;
  }

  set activeTab(value: string) {}

  get activeTab() {
    return this.$route.fullPath;
  }

  statusLabelFor(t: ITranscription) {
    if (!t) return '編集不可';
    if (t.status === 'editing') {
      if (!this.currentUser) return this.$t('trsc.locked');
      return this.isCurrentUserEditing
        ? this.$t('trsc.editing')
        : this.$t('trsc.locked');
    } else {
      switch (t.status) {
        case 'default':
          return this.$t('trsc.default');
        case 'completed':
          return this.$t('trsc.completed');
        case 'frozen':
          return '確定';
        case 'initiated':
          return this.$t('trsc.initiated');
        default:
          return '不明';
      }
    }
  }

  back() {
    if (this.ref) {
      this.$router.push(this.ref);
    } else {
      this.$router.push('/');
    }
  }

  nextPage() {
    this.$router.push({
      name: 'transcription.view',
      params: {
        entryId: this.$route.params.entryId,
        page: this.currentPage + 1 + '',
      },
      query: {
        ref: this.ref,
      },
    });
  }

  previousPage() {
    this.$router.push({
      name: 'transcription.view',
      params: {
        entryId: this.$route.params.entryId,
        page: this.currentPage - 1 + '',
      },
      query: {
        ref: this.ref,
      },
    });
  }

  onTranscriptionSaved(count: number) {
    this.count = count;
    this.showShareDialog = true;
  }

  @Watch('$route.params.page')
  async onPageChange() {
    await trscModule.unbindTranscription();
    const payload = {
      entryId: this.$route.params.entryId,
      index: this.currentPage - 1,
    };
    await trscModule.bindTranscription(payload);
    const label = this.processLabel(trscModule.entry.label);
    document.title = `${label}・コマ${this.$route.params.page} - Minna de Honkoku`;
  }

  @Watch('$route.params.entryId')
  reload() {
    this.$router.go(0);
  }

  async created() {
    await trscModule.bindEntry(this.$route.params.entryId);
    this.onPageChange();
    this.collection = await this.$db.getCollectionData(this.entry.collectionId);
    this.project = await this.$db.getProjectData(this.entry.projectId);
  }

  beforeDestroy() {
    //console.log('unbind');
    trscModule.updateTranscriptionLocally({ text: '' });
    trscModule.unbindTranscription();
    return trscModule.unbindEntry();
  }

  startEditing() {
    this.showStartDialog = true;
  }

  discardEdit() {
    trscModule.discardEdit();
  }

  completeEditing() {
    this.showCompleteDialog = true;
  }
}
